import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  CustomerInfo,
  LOG_LEVEL,
  Purchases,
  PurchasesOffering
} from '@revenuecat/purchases-capacitor';
import { sharedConfig } from 'src/environments/shared-config';
import { UserData } from '../auth-service/user-data.model';
import { ModalController } from '@ionic/angular';
import { UserInfoService } from '../auth-service/user-info-service';
import { EnvironmentConfigService } from '../environment-config-service/environment-config.service';
import { PaywallPopupComponent } from 'src/app/components/paywall-popup/paywall-popup.component';
import { PromptService } from '../promtp-service/prompt.service';
import { TranslatorService } from '../translator-service/translator.service';
import { OrganizationService } from '../organization-service/organization.service';
import { EventService } from '../events/event.service';
import { AuthService } from '../auth-service/auth.service';
import { SystemLogService } from '../systemlog-service/systemlog.service';
import { FacescanService } from '../face-scan-service/facescan.service';
import { Network } from '@capacitor/network';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private personId?: string;
  private isNative = Capacitor.isNativePlatform();

  constructor(
    private modalCtrl: ModalController,
    private _environmentService: EnvironmentConfigService,
    private _promptService: PromptService,
    private _translationService: TranslatorService,
    private _organizationService: OrganizationService,
    private _authService: AuthService,
    private _faceScanService: FacescanService
  ) { }


  public async init() {

    if(!Capacitor.isNativePlatform()) return;

    try {
      const status = await Network.getStatus();
      if (!status.connected) {
        return;
      }

      const subscriptionRequired = await this._organizationService.SubscriptionRequired();
      if (subscriptionRequired !== true) return;

      if (!this.isNative && this._environmentService.IsProduction()) {
        return;
      }

      const lastScan = await this._faceScanService.getLastScanResultStatic();
      if (!lastScan) return;


      const usr = await UserInfoService.getUserInfo();
      if (!usr) return;

      // Configure RevenueCat (with appUserID) once per app start
      await this.initPayCat(usr);

      // Proactively validate on startup (show paywall only when subscription is missing)
      await this.checkAndSubscribe(async (success) => {
        if (!success) await this._authService.logout('Subscription expired');
      }, true);

      // One long-lived listener for future changes
      Purchases.addCustomerInfoUpdateListener(async (updated) => {
        try {
          const stillRequired = await this._organizationService.SubscriptionRequired();
          if (stillRequired !== true) {
            return;
          }

          const active = updated?.entitlements?.active || {};
          const hasActive = Object.keys(active).length > 0;

          if (hasActive) {
            EventService.revcatEntitlementsActive.emit();
            return;
          }
          await this.checkAndSubscribe(async (success) => {
            if (!success) {
              // Your chosen policy on expiry
              setTimeout(async () => { await this._authService.logout('Subscription expired')},3000);
              return;
            }
          }, /* showPaywall */ true);

        } catch (listenerErr) {
          SystemLogService.logError(listenerErr, 'SubscriptionService.init', 'listener failed');
        }
      });

    } catch (err) {
      SystemLogService.logError(err, 'SubscriptionService.init', 'init() failed');
    }
  }

  private async initPayCat(userInfo: UserData): Promise<void> {
    if (!userInfo || !userInfo.tokenClaims || !userInfo.tokenClaims.personId) {
      await this._promptService.showError(
        this._translationService.translateText('subscription-service.errors.person-record-empty')
      );
      this.personId = undefined;
      throw new Error('PersonId not found - user not logged in');
    }

    const env = await this._environmentService.EnsureNetworkConfigurationForToken();
    if (!env || !env.countryCode) {
      await this._promptService.showError(
        this._translationService.translateText('subscription-service.errors.person-record-empty')
      );
      this.personId = undefined;
      throw new Error('Unable to determine region');
    }

    const personId = env.countryCode.toLocaleLowerCase().trim() + '_' + userInfo.tokenClaims.personId;
    if (this.personId === personId) return; // already configured
    this.personId = personId;

    if (this.isNative) {
      let logLevel = LOG_LEVEL.WARN;

      try {
        await Purchases.setLogLevel({ level: logLevel });
        const apiKey =
          Capacitor.getPlatform() === 'ios'
            ? sharedConfig.revenueCatIOSKey
            : sharedConfig.revenueCatAndroidKey;

        await Purchases.configure({
          apiKey,
          appUserID: this.personId,
        });
      } catch (error) {
        this.personId = undefined;
        // Do not crash UI flows; surface a helpful message
        await this._promptService.showError(
          this._translationService.translateText('subscription-service.errors.subscription-status-unavailable')
        );
        throw new Error('Unable to init Purchases Platform');
      }
    }
  }

  private async getOfferings(): Promise<PurchasesOffering | null> {
    if (!this.isNative) {
      return null;
    }
    try {
      const offerings = await Purchases.getOfferings();
      return offerings?.current ?? null;
    } catch {
      // Network/offline/RC errors shouldn’t crash
      return null;
    }
  }

  private async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.isNative) return null;
    try {
      const result = await Purchases.getCustomerInfo();
      return result.customerInfo ?? null;
    } catch {
      return null;
    }
  }

  public async checkAndSubscribe(
    callback: (success: boolean) => void,
    showPaywall: boolean = false
  ): Promise<void> {
    if(!Capacitor.isNativePlatform()) {
      callback(false);
      return;
    };
    
    const status = await Network.getStatus();
    if (!status.connected) {
      callback(true);
      return;
    }
    // 1) Org requirement
    const subscriptionRequired = await this._organizationService.SubscriptionRequired();
    if (subscriptionRequired == null) {
      await this._promptService.showError(
        this._translationService.translateText('subscription-service.errors.subscription-requires-app')
      );
      callback(false);
      return;
    }

    if (subscriptionRequired === false) {
      callback(true);
      return;
    }

    // 2) Allow non-production (UAT) builds on web
    if (!this.isNative && !this._environmentService.IsProduction()) {
      callback(true);
      return;
    }

    // 3) Native required
    if (subscriptionRequired && !this.isNative) {
      await this._promptService.showError(
        this._translationService.translateText('subscription-service.errors.subscription-requires-app')
      );
      callback(false);
      return;
    }

    // 4) Ensure user
    const usr = await UserInfoService.getUserInfo();
    if (!usr) {
      await this._promptService.showError(
        this._translationService.translateText('subscription-service.errors.subscription-status-unavailable')
      );
      callback(false);
      return;
    }

    await this.initPayCat(usr);

    // 5) Start fetching offerings ASAP (in parallel)
    const offeringsPromise = this.getOfferings().catch(() => null);

    // Current entitlements
    let customerInfo = await this.getCustomerInfo();
    let activeEntitlements = customerInfo?.entitlements?.active;
    let alreadySubscribed = !!(activeEntitlements && Object.keys(activeEntitlements).length > 0);

    if (alreadySubscribed && !showPaywall) {
      callback(true);
      return;
    }

    if (!showPaywall) {
      callback(alreadySubscribed);
      return;
    }

    // Build subscription timing info if already subscribed
    let subscriptionStatus: SubscriptionStatus | null = null;
    if (alreadySubscribed && activeEntitlements) {
      const entitlement: any = Object.values(activeEntitlements)[0]; // assumes 1 active entitlement
      subscriptionStatus = {
        purchaseDate: entitlement?.latestPurchaseDate ? new Date(entitlement.latestPurchaseDate) : undefined,
        expirationDate: entitlement?.expirationDate ? new Date(entitlement.expirationDate) : undefined,
        originalPurchaseDate: entitlement?.originalPurchaseDate ? new Date(entitlement.originalPurchaseDate) : undefined,
      };
    }

    // 6) We must await offerings before presenting (since we can't update props later)
    const offerings = await offeringsPromise;

    // RevenueCat entitlement listener to auto-close the modal when entitlements flip
    let entitlementBecameActive = false;
    let listenerHandle: { remove: () => Promise<void> } | undefined;
    try {
      // addCustomerInfoUpdateListener returns a PluginListenerHandle with remove()
      listenerHandle = (Purchases as any).addCustomerInfoUpdateListener?.((info: any) => {
        const active = info?.entitlements?.active || {};
        if (Object.keys(active).length > 0) {
          entitlementBecameActive = true;
          // If modal is open, close it as success
          this.modalCtrl.getTop().then(top => {
            if (top && top.id === 'paywall-modal') {
              top.dismiss({ success: true }, 'success');
            }
          }).catch(() => {/* ignore */ });
        }
      });
    } catch {
      // Listener is optional; flow still works without it
    }

    // 7) Present modal
    let modalRole: string | undefined;
    try {
      const modal = await this.modalCtrl.create({
        id: 'paywall-modal',
        component: PaywallPopupComponent,
        componentProps: {
          offerings,                         // ready now
          isSubscribed: alreadySubscribed,
          subscriptionStatus,
        },
        cssClass: 'paywall-modal',
        animated: true,
      });

      await modal.present();

      const { role } = await modal.onDidDismiss();
      modalRole = role; // 'success' if component dismissed after purchase
    } catch {
      // If presenting fails, we'll rely on the entitlement check below
    } finally {
      // Always remove the listener if we added one
      try { await listenerHandle?.remove?.(); } catch { /* ignore */ }
    }

    // 8) Re-check entitlements after the interaction (or attempt)
    try {
      customerInfo = await this.getCustomerInfo();
      activeEntitlements = customerInfo?.entitlements?.active;
      alreadySubscribed = !!(activeEntitlements && Object.keys(activeEntitlements).length > 0);
    } catch {
      // ignore
    }

    // Success if:
    //  - modal reported 'success', OR
    //  - entitlements flipped via listener, OR
    //  - fresh entitlements now show active
    const success = modalRole === 'success' || entitlementBecameActive || alreadySubscribed === true;
    callback(success);
  }
}

export interface SubscriptionStatus {
  purchaseDate?: Date;
  expirationDate?: Date;
  originalPurchaseDate?: Date;
}